/**
 * YouTube URL Parser Utility
 * Extracts video ID from various YouTube URL formats
 */

/**
 * Extract YouTube video ID from various URL formats
 * Supports:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/v/VIDEO_ID
 * - https://www.youtube.com/shorts/VIDEO_ID
 * - https://m.youtube.com/watch?v=VIDEO_ID
 *
 * @param {string} url - YouTube URL
 * @returns {string|null} - Video ID or null if invalid
 */
export const extractVideoId = (url) => {
    if (!url) return null;

    // Clean the URL
    url = url.trim();

    // Regular expressions for different YouTube URL formats
    const patterns = [
        // Standard watch URL: youtube.com/watch?v=VIDEO_ID
        /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i,
        // Short URL: youtu.be/VIDEO_ID
        /youtu\.be\/([^"&?\/\s]{11})/i,
        // Embed URL: youtube.com/embed/VIDEO_ID
        /youtube\.com\/embed\/([^"&?\/\s]{11})/i,
        // Shorts URL: youtube.com/shorts/VIDEO_ID
        /youtube\.com\/shorts\/([^"&?\/\s]{11})/i,
    ];

    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
            return match[1];
        }
    }

    // If URL is just the video ID (11 characters)
    if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
        return url;
    }

    return null;
};

/**
 * Validate if a string is a valid YouTube video ID
 * @param {string} videoId - Video ID to validate
 * @returns {boolean}
 */
export const isValidVideoId = (videoId) => {
    return videoId && /^[a-zA-Z0-9_-]{11}$/.test(videoId);
};

/**
 * Get YouTube thumbnail URL from video ID
 * @param {string} videoId - YouTube video ID
 * @param {string} quality - Thumbnail quality (default, mqdefault, hqdefault, sddefault, maxresdefault)
 * @returns {string} - Thumbnail URL
 */
export const getThumbnailUrl = (videoId, quality = 'mqdefault') => {
    if (!isValidVideoId(videoId)) return null;
    return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};

/**
 * Get YouTube embed URL from video ID
 * @param {string} videoId - YouTube video ID
 * @returns {string} - Embed URL
 */
export const getEmbedUrl = (videoId) => {
    if (!isValidVideoId(videoId)) return null;
    return `https://www.youtube.com/embed/${videoId}`;
};

/**
 * Get YouTube watch URL from video ID
 * @param {string} videoId - YouTube video ID
 * @returns {string} - Watch URL
 */
export const getWatchUrl = (videoId) => {
    if (!isValidVideoId(videoId)) return null;
    return `https://www.youtube.com/watch?v=${videoId}`;
};

/**
 * Parse a YouTube URL and return video info
 * @param {string} url - YouTube URL
 * @returns {object|null} - Video info object or null
 */
export const parseYouTubeUrl = (url) => {
    const videoId = extractVideoId(url);
    if (!videoId) return null;

    return {
        videoId,
        thumbnailUrl: getThumbnailUrl(videoId, 'mqdefault'),
        embedUrl: getEmbedUrl(videoId),
        watchUrl: getWatchUrl(videoId),
    };
};

export default {
    extractVideoId,
    isValidVideoId,
    getThumbnailUrl,
    getEmbedUrl,
    getWatchUrl,
    parseYouTubeUrl,
};
